import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Anchor, CalendarCheck, ChevronDown, User, LayoutGrid, GraduationCap, Sparkles, Grid3X3 } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { signOutGuest, getGuest, switchActiveGuest, isIpadMode } from "@/lib/guest";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import GuidedTour from "@/components/GuidedTour";
import { GUEST_STEPS, IPAD_STEPS } from "@/lib/sopSteps";

export default function GuestHeader({ guestName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { spaName, logoUrl, ipadLogoUrl, boatName } = useBranding();
  const { cabin, id: currentId, roster } = getGuest() || {};
  const onReservations = location.pathname === "/reservations";
  const onMenu = location.pathname === "/menu";
  const onExperience = location.pathname === "/experience" || location.pathname.startsWith("/service/");
  const onCabin = location.pathname === "/cabin";
  const onJourney = location.pathname === "/journey";
  const ipadMode = isIpadMode();
  // In iPad kiosk mode, prefer the dedicated cabin-iPad icon so the header
  // (and by extension the whole logged-in session) carries the operator's
  // iPad-specific mark. Falls back to the everyday logo when no dedicated
  // icon has been uploaded.
  const headerLogoUrl = ipadMode ? (ipadLogoUrl || logoUrl) : logoUrl;
  const [tourOpen, setTourOpen] = useState(false);
  const tourSteps = ipadMode ? IPAD_STEPS : GUEST_STEPS;

  const doSwitch = (gid) => {
    if (gid === currentId) return;
    switchActiveGuest(gid);
    // reload the current route so all data refetches for the new guest
    window.location.reload();
  };

  const hasMultiple = Array.isArray(roster) && roster.length > 1;

  return (
    <header className="glass sticky top-0 z-40" data-testid="guest-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-4 flex items-center justify-between gap-3">
        <Link to={guestName ? "/experience" : "/"} className="flex items-center gap-3 group min-w-0" data-testid="header-logo">
          {headerLogoUrl ? (
            <img src={headerLogoUrl} alt="Logo" className="h-7 w-7 object-contain flex-shrink-0" data-testid="header-logo-img" />
          ) : (
            <Anchor className="h-5 w-5 text-primary flex-shrink-0" strokeWidth={1.5} />
          )}
          <span className="font-display text-base sm:text-lg tracking-tight truncate">{spaName}</span>
        </Link>
        <nav className="flex items-center gap-3 sm:gap-5 text-sm">
          {ipadMode && !onCabin && (
            <Link to="/cabin" className="inline-flex items-center gap-1.5 hover:text-accent transition-colors text-primary" data-testid="nav-cabin">
              <LayoutGrid className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Cabin view</span>
              <span className="sm:hidden">Cabin</span>
            </Link>
          )}
          {guestName && (
            <>
              <button
                type="button"
                onClick={() => setTourOpen(true)}
                className="inline-flex items-center gap-1.5 text-primary/70 hover:text-accent transition-colors"
                data-testid="guest-quick-learn-btn"
                title="Quick Learn"
              >
                <GraduationCap className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Quick Learn</span>
              </button>
              <Link to="/experience" className={`hidden sm:inline-flex items-center gap-1.5 hover:text-accent transition-colors ${onExperience ? "text-accent" : ""}`} data-testid="nav-experience"><Grid3X3 className="h-4 w-4"/>Services</Link>
              <Link to="/menu" className={`hidden lg:inline hover:text-accent transition-colors ${onMenu ? "text-accent" : ""}`} data-testid="nav-menu">Wellness</Link>
              <Link to="/reservations" className={`inline-flex items-center gap-1.5 hover:text-accent transition-colors ${onReservations ? "text-accent" : ""}`} data-testid="nav-reservations">
                <CalendarCheck className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">My reservations</span>
                <span className="sm:hidden">Mine</span>
              </Link>
              <Link to="/journey" className={`inline-flex items-center gap-1.5 hover:text-accent transition-colors ${onJourney ? "text-accent" : ""}`} data-testid="nav-journey" title="Your private journey">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">My journey</span>
                <span className="sm:hidden">Journey</span>
              </Link>

              {/* Only render the account dropdown when it has something to
                  show. On iPad kiosk mode with a single guest there's
                  nothing left inside (guest-switching hidden, sign-out
                  hidden) and an empty dropdown feels broken. */}
              {(hasMultiple || !isIpadMode()) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="text-right leading-tight hover:opacity-80 transition-opacity flex items-center gap-2"
                    data-testid="guest-name-badge"
                    title="Account"
                  >
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-sm text-primary truncate max-w-[140px]">{guestName}</span>
                      {cabin && <span className="text-[10px] text-primary/40 font-light tracking-wide truncate max-w-[140px]" data-testid="guest-cabin-label">{cabin}</span>}
                    </div>
                    <ChevronDown className="h-3 w-3 text-primary/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {hasMultiple && (
                    <>
                      <DropdownMenuLabel className="overline text-primary/50 text-[10px]">Switch guest ({cabin})</DropdownMenuLabel>
                      {roster.map((r) => (
                        <DropdownMenuItem
                          key={r.guest_id}
                          onClick={() => doSwitch(r.guest_id)}
                          className={r.guest_id === currentId ? "bg-muted" : ""}
                          data-testid={`switch-guest-${r.guest_id}`}
                        >
                          <User className="h-3.5 w-3.5 mr-2 opacity-60" />
                          {r.guest_name}{r.guest_id === currentId && " · (you)"}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isIpadMode() && (
                    <DropdownMenuItem onClick={() => { signOutGuest(); navigate("/"); }} data-testid="sign-out-item">
                      Sign out
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </>
          )}
          {/* No Crew / admin link here on purpose — the guest UI (and
              the shared cabin iPad) must never expose an admin sign-in
              entry point. Admins bookmark /admin/login directly. */}
        </nav>
      </div>
      <GuidedTour
        open={tourOpen}
        steps={tourSteps}
        onClose={() => setTourOpen(false)}
      />
    </header>
  );
}
